// Data types
// Dependencies
import { Component } from 'react';

// Components
import { FlyToInterpolator, WebMercatorViewport } from '@deck.gl/core';
import { GeoJsonLayer } from '@deck.gl/layers';
import {
    DRAWING_LINE_COLOR,
    DRAWING_MODES,
    LAYERS,
    MARKING_LINE_COLOR,
    OUTLINE_INTERVALS,
    TOOLTIP_OFFSET,
} from 'data/static';
import { EditableGeoJsonLayer } from 'nebula.gl';
import {
    BackgroundType,
    CoordinateArray,
    DeckGLLayer,
    LayerTypeData,
    MapProps,
    MoveEvent,
    PlanType,
    PortAreaType,
    PortBackgroundType,
    PositionType,
    RGBAColor,
    ToolTipType,
    ViewState,
} from 'types';
import {
    ClearToolTipsInterface,
    CreateDrawingLayerInterface,
    CreateMarkingLayerInterface,
    FlagTooltipIdInterface,
    GetDrawingFeaturesInterface,
    GetTooltipsInterface,
    HideTooltipByIdInterface,
    HideToolTipsInterface,
    MoveTooltipWithTimeInterface,
    PortAreaFeatureVisiblityInterface,
    RefreshFeaturesInterface,
    RemoveDrawingByIdInterface,
    removeDrawingToolTipByIdInterface,
    SetDrawingModeInterface,
    SetTooltipsInterface,
} from 'types/functions';
import { GeoJSONFeature } from 'types/geojson';
import { hexToRgb } from 'utils/colors';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { checkDifferent, clone, getId } from 'utils/formHandlers';
import {
    getLayerProvider,
    getScreenCoordinates,
    removeLayer,
    updateFeatureProperties,
    updatePortAreaFeatureStyle,
} from 'utils/layers';
import { formatTooltips } from 'utils/migrations';
import { translate } from 'utils/translate';

export default class BaseMap<P extends { [key: string]: any }, S extends { [key: string]: any }> extends Component<
    P,
    S
> {
    public geometry;

    /**
     * Handles the drag event on the map, updating the tooltips.
     *
     * @param {MoveEvent} e - The event triggered on drag.
     */
    handleOnDrag = (i: any, e: MouseEvent) => {
        this.moveToolTips();
    };

    /**
     * Handles the drag start event on the map, hiding tooltips.
     *
     * @param {MoveEvent} e - The event triggered on drag start.
     */
    handleOnDragStart = (i: any, e: MouseEvent) => {
        // this.hideTooltips(['onHover']);
    };

    /**
     * Handles the drag end event on the map, hiding tooltips.
     *
     * @param {MoveEvent} e - The event triggered on drag end.
     */
    handleOnDragEnd = (i: any, e: MouseEvent) => {
        // this.hideTooltips(['onHover', 'onClick']);
    };

    /**
     * Handles the hover event on the map, updating the coordinate and hiding tooltips.
     *
     * @param {MoveEvent} e - The event triggered on hover.
     */
    handleOnHover = (i: any, e: MouseEvent): any => {
        if (
            (this.state.drawingMode === 'drawLineStringMode' || this.state.drawingMode === 'measureLengthMode') &&
            this.state.hoverMeasureData !== null &&
            i.coordinate
        ) {
            //@ts-ignore
            this.setState({
                coordinate: i.coordinate,
                hoverMeasureData: {
                    ...this.state.hoverMeasureData,
                    x: i.x,
                    y: i.y,
                    length: this.geometry.getLength([
                        this.state.hoverMeasureData?.coordinates,
                        this.geometry.transformer(i.coordinate, 'EPSG:4326', this.props.port.projection),
                    ]),
                    bearing: this.geometry.getBearing([
                        this.state.hoverMeasureData?.coordinates,
                        this.geometry.transformer(i.coordinate, 'EPSG:4326', this.props.port.projection),
                    ]),
                },
            });
            this.hideTooltips(['onHover']);
            return;
        }
        this.setState({ coordinate: i.coordinate });
        this.hideTooltips(['onHover']);
    };

    /**
     * Handles actions to be performed after the map has rendered, such as hiding tooltips.
     */
    handleAfterRender = () => {
        // this.hideTooltips(['onHover', 'onClick']);
    };

    /**
     * Handles changes in the view state, updating the map and tooltips.
     *
     * @param {Object} param0 - The new view state object.
     */
    handleViewStateChange = ({ viewState }: any) => {
        if (!this.state.mapProps) {
            return;
        }
        // this.state.map?.setProps({ viewState });
        //@ts-ignore
        this.setState({ mapProps: { ...this.state.mapProps, viewState } });
        this.state.map?.setProps({ viewState });
        this.moveToolTips(viewState);
    };

    /**
     * Handles the click event on the map, showing tooltips for selected layers.
     * @param {any} info - Information about the clicked object.
     * @param {MouseEvent} event - The mouse event for the click.
     */
    handleOnClick = (info: any, event: MouseEvent) => {
        console.time('handleOnClick total');

        console.time('step B 1: extract + transform');
        const { coordinate, x, y } = info;
        const coordinate_utm = this.geometry.transformer(coordinate, 'EPSG:4326', this.props.port.projection);
        console.timeEnd('step B 1: extract + transform');

        console.time('step B 2: pick objects');
        const pickInfos: any[] = this.state.map?.pickMultipleObjects({ x, y, radius: 1, depth: 100 });
        console.timeEnd('step B 2: pick objects');

        console.time('step B 3: filter + map layersData');
        const layersData = pickInfos
            .filter((x) => {
                return (
                    (x.object?.properties?.index !== null && x.object?.properties?.visible) ||
                    x.layer.id === LAYERS.drawingLayer.uuid
                );
            })
            .map((x) => {
                return {
                    layerGroup: x.object.properties.layerGroup,
                    groupCount: x.object.properties.groupCount,
                    layer: x.object.properties.layer,
                    drawIndex: x.object.properties.drawIndex,
                    clickIndex: x.object.properties.clickIndex,
                    name: x.object.properties.name,
                    parent: x.object.properties.parent,
                    dataIndex: x.object.properties.dataIndex,
                };
            })
            .sort((a, b) => b.clickIndex - a.clickIndex);
        console.timeEnd('step B 3: filter + map layersData');

        console.time('step B 4: drawingMode check');
        if (this.state.drawingMode !== 'pointer') {
            if (layersData.length === 0) {
                //@ts-ignore
                this.setState((prevState) => {
                    const updatedFeatures = prevState.drawingFeatures.features.map((feature: any) => ({
                        ...feature,
                        properties: {
                            ...feature.properties,
                            selected: false,
                        },
                    }));

                    return {
                        hoverMeasureData: {
                            coordinates: this.geometry.transformer(coordinate, 'EPSG:4326', this.props.port.projection),
                        },
                        drawingFeatures: {
                            type: prevState.drawingFeatures.type,
                            features: updatedFeatures,
                        },
                    };
                });
            }
            console.timeEnd('step B 4: drawingMode check');
            console.timeEnd('handleOnClick total');
            return;
        }
        console.timeEnd('step B 4: drawingMode check');

        console.time('step B 5: layersData empty check');
        if (layersData.length === 0) {
            this.afterClick(0, null);
            //@ts-ignore
            this.setState((prevState) => {
                const updatedFeatures = prevState.drawingFeatures.features.map((feature: any) => ({
                    ...feature,
                    properties: {
                        ...feature.properties,
                        selected: false,
                    },
                }));

                return {
                    drawingFeatures: {
                        type: prevState.drawingFeatures.type,
                        features: updatedFeatures,
                    },
                };
            });
            this.hideTooltips();
            console.timeEnd('step B 5: layersData empty check');
            console.timeEnd('handleOnClick total');
            return;
        }
        console.timeEnd('step B 5: layersData empty check');

        const { tooltips } = this.state;

        console.time('step B 6: find maxIndex');
        console.log(layersData);
        const maxIndex = layersData.reduce((max, item) => {
            return item.clickIndex > max ? item.clickIndex : max;
        }, 0);
        console.timeEnd('step B 6: find maxIndex');

        // static id for portAreas tooltip
        let tooltip: any = {};
        let uuid = getId();

        if (maxIndex === 0) {
            console.timeEnd('handleOnClick total');
            return;
        } else if (maxIndex <= 100) {
            console.time('step B 7: build portAreas tooltip');
            uuid = '96136683-7596-488f-b837-28fa68bc3e63';
            tooltip = {
                static: false,
                uuid,
                type: 'portAreas',
                event: 'onClick',
                bind: false,
                longitude: coordinate[0],
                latitude: coordinate[1],
                east_utm: coordinate_utm[0],
                north_utm: coordinate_utm[1],
                x: x + TOOLTIP_OFFSET,
                y: y + TOOLTIP_OFFSET,
                xOffset: 0,
                yOffset: 0,
                data: layersData,
                visible: true,
            };
            this.afterClick(0, null);
            console.timeEnd('step B 7: build portAreas tooltip');
        } else {
            console.time('step B 7: build other tooltip + nearest point');
            const selectedFeature = layersData.filter((x) => {
                return x.clickIndex === maxIndex;
            });
            let dataIndex = selectedFeature[0]?.dataIndex;

            if (!dataIndex) {
                let nearestPoint: PositionType | null;
                if (this.state.mode === 'search') {
                    const { transit } = this.props.mainControls.getTransit(selectedFeature[0].parent);
                    nearestPoint = this.props.mainControls.getBestPosition(transit.id, null, {
                        east_utm: coordinate_utm[0],
                        north_utm: coordinate_utm[1],
                    });
                    dataIndex = {
                        type: transit.best,
                        index: nearestPoint?.id,
                        time: this.props.dateTimeFormat.parseDateTime(nearestPoint?.valid_time_utc),
                    };
                } else {
                    const track =
                        this.state.mode === 'playback'
                            ? this.props.menuControls.playback.getTrack(selectedFeature[0].parent)
                            : this.props.menuControls.time.getTrack(selectedFeature[0].parent);

                    uuid = track.uuid;
                    nearestPoint =
                        this.state.mode === 'playback'
                            ? this.props.menuControls.playback.getBestPosition(track.uuid, null, {
                                  east_utm: coordinate_utm[0],
                                  north_utm: coordinate_utm[1],
                              })
                            : this.props.menuControls.time.getBestPosition(track.uuid, null, {
                                  east_utm: coordinate_utm[0],
                                  north_utm: coordinate_utm[1],
                              });

                    dataIndex = {
                        index: nearestPoint?.id,
                        time: nearestPoint?.valid_time_utc,
                    };
                }
            }

            tooltip = {
                static: false,
                uuid: uuid,
                type: selectedFeature[0].layer,
                event: 'onClick',
                bind: false,
                longitude: coordinate[0],
                latitude: coordinate[1],
                east_utm: coordinate_utm[0],
                north_utm: coordinate_utm[1],
                x: x + TOOLTIP_OFFSET,
                y: y + TOOLTIP_OFFSET,
                xOffset: 0,
                yOffset: 0,
                dataIndex: dataIndex,
                data: selectedFeature[0],
                visible: true,
            };
            console.timeEnd('step B 7: build other tooltip + nearest point');

            console.time('step B 8: matrix / afterClick logic');
            if (
                maxIndex === LAYERS.sweptPath.clickIndex ||
                maxIndex === LAYERS.transitTrack.clickIndex ||
                (maxIndex === LAYERS.vesselOutline.clickIndex && this.state.mode === 'search')
            ) {
                this.createClickMarix(
                    layersData
                        .filter((layerData) => {
                            return layerData.clickIndex === maxIndex;
                        })
                        .map((layerData) => {
                            const nearestPoint = this.props.mainControls.getBestPosition(layerData.parent, null, {
                                east_utm: coordinate_utm[0],
                                north_utm: coordinate_utm[1],
                            });

                            const { transit } = this.props.mainControls.getTransit(layerData.parent);
                            const currentTime = this.props.dateTimeFormat.parseDateTime(nearestPoint.valid_time_utc);
                            const transitStart = this.props.dateTimeFormat.parseDateTime(transit.start_utc);

                            return {
                                ...layerData,
                                skipInterval: OUTLINE_INTERVALS.map((y) => {
                                    return currentTime.getDiffInSeconds(transitStart) % y;
                                }),

                                currentTime: currentTime,
                                //@ts-ignore
                                nearestPoint: nearestPoint.id,
                            };
                        })
                );

                this.afterClick(selectedFeature[0].parent, dataIndex.time);
            } else if (maxIndex === LAYERS.vesselOutline.clickIndex) {
                this.setState(
                    {
                        clickMatrix: this.state.clickMatrix.map((matrix) => {
                            if (matrix.selected) {
                                return { ...matrix, index: selectedFeature[0].dataIndex.i };
                            }
                            return matrix;
                        }),
                    },
                    () => {
                        // @ts-ignore
                        this.updateVesselsOulines(false);
                    }
                );
            } else {
                this.afterClick(selectedFeature[0].parent, dataIndex.time);
            }
            console.timeEnd('step B 8: matrix / afterClick logic');
        }

        console.time('step B 9: update tooltips state');
        const findTooltip = tooltips.find((x) => {
            return (
                x.uuid === uuid ||
                (x.data.parent === tooltip.data.parent && x.data.layerGroup === tooltip.data.layerGroup)
            );
        });

        if (!findTooltip) {
            //@ts-ignore
            this.setState({
                tooltips: [...tooltips.filter((x) => x.static), { ...tooltip }],
            });
        } else {
            this.setState({
                tooltips: tooltips.map((x) => {
                    if (x.uuid === findTooltip.uuid) {
                        return { ...tooltip, bind: findTooltip.bind, static: findTooltip.static };
                    }
                    return x;
                }),
            });
        }
        console.timeEnd('step B 9: update tooltips state');

        console.timeEnd('handleOnClick total');
    };

    setClickMatrix = (clickMatrix: any): Promise<void> => {
        return new Promise((resolve) => {
            this.setState(
                {
                    clickMatrix: clickMatrix,
                },
                resolve // Callback called after state is updated
            );
        });
    };

    getClickMatrix = (transitId: number) => {
        if (transitId === 0) {
            return this.state.clickMatrix;
        }

        const matrix = this.state.clickMatrix.find((x) => {
            return x.transitId === transitId && x.selected;
        });

        if (matrix) {
            return matrix;
        }

        return {
            selected: true,
            transitId: transitId,
            positionId: null,
            skipInterval: [0, 0, 0, 0, 0, 0],
            index: 0,
            min: 0,
            max: null,
        };
    };

    isSelectedIndex = (
        transitId: number,
        positions: PositionType[],
        positionIndex: number,
        updateTooltip = false
    ): boolean => {
        const matrix = this.getClickMatrix(transitId);

        // If no matching matrix entry, return false
        if (!matrix) return false;

        // Direct match with the stored index
        if (matrix.index === positionIndex) {
            if (updateTooltip) {
                //@ts-ignore
                this.moveTooltipWithTime(positions[positionIndex], 'sweptPath', transitId);
                //@ts-ignore
                this.moveTooltipWithTime(positions[positionIndex], 'vesselOutline', transitId);
            }
            return true;
        }
        //@ts-ignore
        if (matrix.index !== -1) return false;

        // Find the index of the closest id to the given positionId
        const nearestIndex = positions.reduce((closestIndex, item, index, array) => {
            const closestDiff = Math.abs(array[closestIndex].id - matrix.positionId);
            const currentDiff = Math.abs(item.id - matrix.positionId);
            return currentDiff < closestDiff ? index : closestIndex;
        }, 0);

        // Return true only if the given index is the closest
        if (nearestIndex === positionIndex) {
            this.setState({
                clickMatrix: this.state.clickMatrix.map((matrix) => {
                    if (!matrix.selected) {
                        return matrix;
                    }
                    return { ...matrix, index: nearestIndex, min: 0, max: positions.length - 1 };
                }),
            });
            return true;
        }

        return false;
    };

    private updateOutlineTimeout: ReturnType<typeof setTimeout> | null = null;

    updateMatrixIndex = (event: string): void => {
        const triggerFinalUpdate = () => {
            if (this.updateOutlineTimeout) {
                clearTimeout(this.updateOutlineTimeout);
            }
            // delay final true update (user stopped pressing keys)
            this.updateOutlineTimeout = setTimeout(() => {
                //@ts-ignore
                this.updateVesselsOulines(true);
            }, 300); // adjust debounce delay (ms) as needed
        };

        switch (event) {
            case 'ArrowUp':
                if (this.state.clickMatrix.length < 2) {
                    return;
                }

                this.setState(
                    (prevState) => {
                        const currentIndex = prevState.clickMatrix.findIndex((m) => m.selected);
                        const nextIndex =
                            currentIndex === -1 || currentIndex === prevState.clickMatrix.length - 1
                                ? 0
                                : currentIndex + 1;

                        const updatedMatrix = prevState.clickMatrix.map((m, index) => ({
                            ...m,
                            selected: index === nextIndex,
                        }));

                        return { clickMatrix: updatedMatrix };
                        //@ts-ignore
                    },
                    () => {
                        this.hideTooltips();
                        this.props.mainControls.inspectTransit(
                            this.state.clickMatrix.find((x) => {
                                return x.selected;
                            }).transitId
                        );
                    }
                );

                break;

            case 'ArrowDown':
                if (this.state.clickMatrix.length < 2) {
                    return;
                }

                this.setState(
                    (prevState) => {
                        const currentIndex = prevState.clickMatrix.findIndex((m) => m.selected);
                        const prevIndex = currentIndex <= 0 ? prevState.clickMatrix.length - 1 : currentIndex - 1;

                        const updatedMatrix = prevState.clickMatrix.map((m, index) => ({
                            ...m,
                            selected: index === prevIndex,
                        }));

                        return { clickMatrix: updatedMatrix };
                        //@ts-ignore
                    },
                    () => {
                        this.hideTooltips();
                        this.props.mainControls.inspectTransit(
                            this.state.clickMatrix.find((x) => {
                                return x.selected;
                            }).transitId
                        );
                    }
                );
                break;
            case 'ArrowLeft':
                this.setState(
                    {
                        clickMatrix: this.state.clickMatrix.map((matrix) => {
                            if (matrix.selected) {
                                const newIndex = matrix.index - 1;
                                if (newIndex < matrix.min) {
                                    return matrix;
                                }
                                return { ...matrix, index: newIndex };
                            }
                            return matrix;
                        }),
                    },
                    () => {
                        //@ts-ignore
                        this.updateVesselsOulines(false); // fast update
                        triggerFinalUpdate();
                    }
                );
                break;

            case 'ArrowRight':
                this.setState(
                    {
                        clickMatrix: this.state.clickMatrix.map((matrix) => {
                            if (matrix.selected) {
                                const newIndex = matrix.index + 1;
                                if (newIndex > matrix.max) {
                                    return matrix;
                                }
                                return { ...matrix, index: newIndex };
                            }
                            return matrix;
                        }),
                    },
                    () => {
                        //@ts-ignore
                        this.updateVesselsOulines(false); // fast update
                        triggerFinalUpdate();
                    }
                );
                break;
            case 'Cancel':
                // @ts-ignore
                this.setState({
                    clickMatrix: this.state.clickMatrix.map((matrix) => {
                        if (matrix.selected) {
                            return { ...matrix, index: -1 };
                        }
                        return matrix;
                    }),
                });
                break;
        }
    };
    createClickMarix = (layers: any[]) => {
        console.log('createClickMarix');
        this.setState(
            //@ts-ignore
            {
                clickMatrix: layers
                    .filter((x) => {
                        return x.layer === 'sweptPath' || x.layer === 'vesselOutline';
                    })
                    .map((x, i) => {
                        return {
                            selected: i === 0,
                            currentTime: x.currentTime,
                            transitId: x.parent,
                            positionId: x.nearestPoint,
                            skipInterval: x.skipInterval,
                            index: x.layer === 'vesselOutline' ? x.dataIndex.i : -1,
                            min: 0,
                            max: x.groupCount || 0,
                        };
                    }),
            },
            () => {
                // @ts-ignore
                this.updateVesselsOulines(false);
            }
        );
    };

    afterClick = (id: number, time: DateTimeFormat | null): void => {
        // afterClick functon interface
    };

    getTooltips: GetTooltipsInterface = () => {
        return this.state.tooltips;
    };

    setTooltips: SetTooltipsInterface = (tooltips: ToolTipType[]) => {
        //@ts-ignore
        this.setState({
            tooltips: formatTooltips(tooltips, this.props.dateTimeFormat),
        });
    };

    getDrawingCount = () => {
        return this.state.drawingFeatures?.features?.length || 0;
    };

    /**
     * Hides tooltips based on the provided events.
     * If no events are provided, no tooltips are hidden.
     * If events are provided, only tooltips that match the events will be hidden.
     *
     * @param {string[]} [events] - The events to filter tooltips by. If undefined, no filtering occurs.
     */
    hideTooltips: HideToolTipsInterface = (events?: string[]) => {
        if (
            this.state.tooltips.filter((tooltip) => {
                return events?.includes(tooltip.event);
            }).length === 0 &&
            events !== undefined
        ) {
            return;
        }
        this.setState({
            tooltips: this.state.tooltips.filter((tooltip) => {
                if ((events !== undefined && !events?.includes(tooltip.event)) || tooltip.bind) {
                    return true;
                }
            }),
        });
    };

    zoomIn = () => {
        const newZoom = this.state.mapProps.viewState.zoom + 1;
        console.log(newZoom);
        if (newZoom > 18) {
            return false;
        }

        this.setState(
            //@ts-ignore
            {
                mapProps: {
                    ...this.state.mapProps,
                    viewState: {
                        ...this.state.mapProps?.viewState,

                        zoom: newZoom,
                    },
                },
            },
            () => {
                this.state.map?.setProps({
                    viewState: this.state.mapProps?.viewState,
                });
            }
        );
    };

    zoomOut = () => {
        const newZoom = this.state.mapProps.viewState.zoom - 1;
        console.log(newZoom);
        if (newZoom < 10) {
            return false;
        }

        this.setState(
            //@ts-ignore
            {
                mapProps: {
                    ...this.state.mapProps,
                    viewState: {
                        ...this.state.mapProps?.viewState,

                        zoom: newZoom,
                    },
                },
            },
            () => {
                this.state.map?.setProps({
                    viewState: this.state.mapProps?.viewState,
                });
            }
        );
    };

    hideTooltipById: HideTooltipByIdInterface = (id?: string) => {
        console.log('hideTooltipById');
        if (!id) {
            // console.warn('WARN: No ID provided to hideTooltipById');
            return;
        }

        this.setState({
            tooltips: this.state.tooltips.filter((tooltip: ToolTipType) => {
                // Keep tooltips that don't match the given id
                return tooltip.uuid !== id;
            }),
        });
    };

    flagTooltipId: FlagTooltipIdInterface = (id?: string) => {
        console.log('flagTooltipId');
        if (!id) {
            console.warn('WARN: No ID provided to hideTooltipById');
            return;
        }

        console.log(`INFO: static tooltip with ID: ${id}`);
        this.setState({
            tooltips: this.state.tooltips.map((tooltip: ToolTipType) => {
                // Keep tooltips that don't match the given id
                if (tooltip.uuid === id) {
                    tooltip.static = !tooltip.static;
                    tooltip.bind = !tooltip.bind;
                }

                return tooltip;
            }),
        });
    };

    /**
     * Clears all tooltips by setting the tooltips state to an empty array.
     * This removes any displayed tooltips from the UI.
     */
    clearTooltips: ClearToolTipsInterface = () => {
        console.log('clearTooltips');
        window?.log('INFO : clear tooltips');
        //@ts-ignore
        this.setState({ tooltips: [] });
    };

    /**
     * Updates the position of the tooltips based on the current view state of the map.
     * It filters out tooltips that are not bound to a specific location and recalculates
     * the screen coordinates for the remaining tooltips. The updated positions are then
     * saved in the component's state.
     *
     * @param {ViewState} viewState - The current state of the map view (e.g., zoom, center, etc.).
     */
    moveToolTips = (viewState?: ViewState) => {
        if (!viewState) {
            viewState = this.state.map.viewManager.getViewState();
        }
        this.setState({
            tooltips: this.state.tooltips
                // .filter((tooltip) => {
                //     return tooltip.bind;
                // })
                .map((tooltip) => {
                    let { x, y } = getScreenCoordinates(tooltip.longitude, tooltip.latitude, viewState);
                    if (!tooltip.xOffset) {
                        x = x + TOOLTIP_OFFSET;
                    } else {
                        // x = tooltip.xOffset / 2 + x;
                    }
                    if (!tooltip.yOffset) {
                        y = y + TOOLTIP_OFFSET;
                    } else {
                        //  y = tooltip.yOffset / 2 + y;
                    }

                    return {
                        ...tooltip,
                        x,
                        y,
                    };
                }),
        });
    };

    /**
     * Retrieves the background configurations for the port, either from localStorage or the port properties.
     *
     * - If data is found in localStorage for the specific port, it is returned.
     * - If no data is found in localStorage, it uses `port_backgrounds` from `props.port`, processes the data,
     *   sorts by background type, assigns statuses, and saves it back to localStorage.
     *
     * @returns {BackgroundType[]} An array of background objects for the port, sorted and with statuses.
     */
    getPortBackgrounds = (mode) => {
        const storedBackgrounds = JSON.parse(localStorage.getItem('backgrounds') || '{}');

        if (storedBackgrounds[`P-${this.props.port?.id}`]) {
            const storedPortBackgrounds = storedBackgrounds[`P-${this.props.port?.id}`];
            if (storedPortBackgrounds[mode]) {
                return storedPortBackgrounds[mode];
            }
        }

        if (!this.props.port.port_backgrounds) {
            return [];
        }

        const modes = { chart: 'ENC', playback: 'ENC', time: 'map' };
        const systemBackgrounds = {};

        Object.keys(modes).forEach((x) => {
            const backgrounds =
                this.props.port?.port_backgrounds
                    .map((background: PortBackgroundType): BackgroundType => {
                        return { ...background.background };
                    })
                    .sort((background: BackgroundType) => (background.type === 'basemap' ? -1 : 1))
                    .map((background: BackgroundType, index: number): BackgroundType => {
                        return { ...background, status: background.name === modes[x] ? 'active' : 'inactive' };
                    }) || [];

            systemBackgrounds[x] = backgrounds;
        });

        this.saveBackgrounds(systemBackgrounds, null);
        return systemBackgrounds[mode];
    };

    /**
     * Changes the active background overlay on the map.
     * This involves updating the background's status in the state, saving the updated background list to `localStorage`,
     * removing the old background layer, and adding the new one to the map.
     *
     * @param {BackgroundType} background - The new background to be activated.
     */
    changeBackground = (background: BackgroundType, mode: string, save = true) => {
        const { mapProps, backgrounds } = this.state;
        const currentBackground = mapProps?.layers?.find((x) => {
            return x.props.type === 'basemap';
        });

        this.setState(
            {
                backgrounds: backgrounds.map((x) => {
                    if (x.type === 'basemap') {
                        x.status = x.id === background.id ? 'active' : 'inactive';
                    }
                    return x;
                }),
            },

            async () => {
                // save backgrounds to localStorage
                if (save) {
                    this.saveBackgrounds(this.state.backgrounds, mode);
                }
                let layers = mapProps?.layers || [];
                // remove old background
                layers = removeLayer(layers, currentBackground?.id);
                // add new background
                //@ts-ignore
                layers = this.addLayer(layers, await getLayerProvider(this.state.coordinate, background, 1));
                // render the map
                await this.renderMap({ layers: layers });
            }
        );
    };

    /**
     * Saves the list of background overlays to `localStorage` under a key based on the port's ID.
     * It updates the existing backgrounds for the given port or adds new ones if none exist.
     *
     * @param {object} backgrounds - The updated list of background overlays to save.
     */
    saveBackgrounds = (backgrounds: object, mode: string | null) => {
        console.log('saveBackgrounds');
        const storedBackgrounds = JSON.parse(localStorage.getItem('backgrounds') || '{}');
        if (mode) {
            if (!storedBackgrounds[`P-${this.props.port?.id}`]) {
                storedBackgrounds[`P-${this.props.port?.id}`] = {};
            }

            storedBackgrounds[`P-${this.props.port?.id}`][mode] = backgrounds;
        } else {
            storedBackgrounds[`P-${this.props.port?.id}`] = backgrounds;
        }
        localStorage.setItem(`backgrounds`, JSON.stringify(storedBackgrounds));
    };

    /**
     * Toggles the visibility of a background overlay layer on the map by its `id`.
     * If the overlay is active, it will be removed, and its status will be set to 'inactive'.
     * If the overlay is inactive, it will be added, and its status will be set to 'active'.
     *
     * @param {BackgroundType} background - The background overlay to toggle on or off.
     */
    changeOverlay = (background: BackgroundType, mode, save = true) => {
        const { mapProps, backgrounds } = this.state;
        let layers = mapProps?.layers || [];
        this.setState(
            {
                backgrounds: backgrounds.map((x) => {
                    if (x.id === background.id) {
                        if (x.status === 'active') {
                            layers = removeLayer(layers, `B-${background.id}`);
                            x.status = 'inactive';
                        } else {
                            //@ts-ignore
                            layers = this.addLayer(layers, getLayerProvider(this.state.coordinate, background, 2));
                            x.status = 'active';
                        }
                    }
                    return x;
                }),
            },
            () => {
                // save backgrounds to localStorage
                if (save) {
                    this.saveBackgrounds(this.state.backgrounds, mode);
                }
                this.renderMap({ layers });
            }
        );
    };

    /**
     * Adds a new layer to an existing array of Deck.gl layers and sorts the combined array by the `index` property.
     *
     * @param {DeckGLLayer[]} layers - The original array of Deck.gl layers.
     * @param {DeckGLLayer} layer - The single new layer to add to the existing layers array.
     *
     * @returns {DeckGLLayer[]} - The new array of layers, with the new layer added and sorted by index.
     */
    addLayer = (layers: DeckGLLayer[], layer: DeckGLLayer) => {
        console.log(`INFO : adding layer with id ${layer.id} index ${layer.props.index}`);

        return [...layers, layer].sort(function (a, b) {
            if (a.props.index < b.props.index) return -1;
            if (a.props.index > b.props.index) return 1;
            return 0;
        });
    };

    /**
     * Adds new layers to an existing array of Deck.gl layers and sorts the combined array by the `index` property.
     *
     * @param {DeckGLLayer[]} layers - The original array of Deck.gl layers.
     * @param {DeckGLLayer[]} newLayers - The array of new layers to add to the existing layers array.
     *
     * @returns {DeckGLLayer[]} - The new array of layers, with `newLayers` added to `layers` and sorted by index.
     */
    addLayers = (layers: DeckGLLayer[], newLayers: DeckGLLayer[]) => {
        return [...layers, ...newLayers].sort(function (a, b) {
            if (a.props.index < b.props.index) return -1;
            if (a.props.index > b.props.index) return 1;
            return 0;
        });
    };

    /**
     * Updates the provided array of Deck.gl layers by replacing or adding layers from a new set of layers.
     *
     * Workflow:
     * 1. Iterates through each `newLayer` in the `newLayers` array.
     *    - If a layer with the same `id` exists in the original `layers` array, it is replaced.
     *    - If no matching `id` is found, the `newLayer` is added to the array.
     * 2. Sorts the updated `layers` array by the `index` property of each layer to maintain rendering order.
     *
     * Sorting Rules:
     * - Layers with lower `index` values are rendered below those with higher `index` values.
     *
     * @param {DeckGLLayer[]} layers - The original array of Deck.gl layers to update.
     * @param {DeckGLLayer[]} newLayers - The array of new Deck.gl layers to replace or add.
     *
     * @returns {DeckGLLayer[]} - The updated and sorted array of Deck.gl layers.
     */
    replaceLayers = (layers: DeckGLLayer[], newLayers: DeckGLLayer[], force = false) => {
        // Create a shallow copy of the layers array to avoid mutating the original
        let updatedLayers = [...layers];

        // Iterate over newLayers and replace, add, or remove layers in the updatedLayers array
        newLayers.forEach((newLayer) => {
            const index = updatedLayers.findIndex((layer) => layer.id === newLayer.id);

            if (!newLayer.props.data || newLayer.props.data.length === 0) {
                // Remove the layer if data is empty
                if (index !== -1) {
                    console.log(`EVENT Layer ${newLayer.id} is removed due to empty data`, 'color: orange');
                    updatedLayers.splice(index, 1); // Remove the layer from the array
                }
            } else if (index !== -1) {
                // Replace the layer if it exists and data has changed
                if (checkDifferent(updatedLayers[index].props.data, newLayer.props.data) || force) {
                    window?.log(`EVENT Layer ${newLayer.id} updated`, 'color: orange');
                    updatedLayers[index] = newLayer;
                }
            } else {
                // Add the layer if it doesn't exist
                window?.log(`EVENT Layer ${newLayer.id} is created`, 'color: orange');
                updatedLayers.push(newLayer);
            }
        });

        // Sort the updatedLayers array by index
        updatedLayers = updatedLayers.sort((a, b) => a.props.index - b.props.index);

        return updatedLayers; // Return the updated array
    };

    /**
     * Updates the map state and triggers a re-render of the map with new properties.
     *
     * This function merges the provided `mapProps` with the existing map properties in the component state.
     * It ensures that the Deck.gl map instance is updated with the new properties and redraws the map
     * to reflect any changes.
     *
     * @param {MapProps} mapProps - The new properties to apply to the map.
     *
     * Workflow:
     * 1. Updates the `mapProps` in the component's state.
     * 2. Calls `setProps` on the Deck.gl map instance to apply the new properties.
     * 3. Redraws the map by invoking `redraw(true)` to ensure visual updates.
     *
     * Note:
     * - `setProps` is used to incrementally update the map without reinitializing it.
     * - `redraw(true)` ensures a complete re-render, which may include recalculating layers.
     */
    renderMap = (mapProps: MapProps): Promise<void> => {
        console.log('renderMap');
        return new Promise((resolve) => {
            const { map } = this.state;

            if (map) {
                map.setProps(mapProps); // Update Deck.gl props
                map.redraw(true); // Explicitly trigger a redraw
            }

            // Update state and resolve the promise
            //@ts-ignore
            this.setState({ mapProps: { ...this.state.mapProps, ...mapProps } }, () => {
                resolve(); // Resolve after state is updated
            });
        });
    };

    hidePilotagePlanFeatures = (areaFeatures: GeoJSONFeature[]) => {
        console.log(111);
        return areaFeatures.map((feature) => {
            //@ts-ignore
            if (!feature.properties?.withPlan && feature.properties.layer !== 'markerPointsLayer') {
                return feature;
            } else if (feature.properties?.layer === 'markerPointsLayer') {
                return {
                    type: feature.type,
                    geometry: feature.geometry,
                    properties: { ...feature.properties, visible: this.state.switchs.markerPoints.value },
                };
            }

            return {
                type: feature.type,
                geometry: feature.geometry,
                properties: { ...feature.properties, visible: false },
            };
        });
    };

    updatePortAreas = () => {
        console.log('updatePortAreas');
        const { switchs } = this.state;

        const areaFeatures: GeoJSONFeature[] = clone(this.state.areaFeatures);

        this.setState(
            //@ts-ignore
            {
                areaFeatures: areaFeatures.map((feature) => {
                    //@ts-ignore
                    if (feature.properties.withPlan) {
                        return feature;
                    }

                    let visible = false;
                    if (switchs[feature.properties?.type]) {
                        visible = switchs[feature.properties?.type].value;
                    } else if (feature.properties?.type === 'berthLine') {
                        visible = switchs['berth'].value;
                    } else {
                        visible = switchs.other.value;
                    }

                    return {
                        type: feature.type,
                        geometry: feature.geometry,
                        properties: {
                            ...feature.properties,
                            visible: visible,
                        },
                    };
                }),
            },
            this.renderPortAreas
        );
    };

    refreshFeatures: RefreshFeaturesInterface = () => {
        //@ts-ignore
        this.setState({ refreshFeatures: getId() });
    };

    getActivePlanFeatures = (oldPilotagePlansState: PlanType[]): { show: string[]; hide: string[] } => {
        const { pilotagePlans, pilotagePlanSettings } = this.state;

        if (pilotagePlans.length === 0) {
            return { show: [], hide: [] };
        }

        const activePlans = pilotagePlans.filter((plan) => plan.value);
        const activeOldPlans = oldPilotagePlansState.filter((plan) => plan.value);

        // Fallback: no active plans → use old hiddenWaypoints
        if (activePlans.length === 0) {
            const oldHidden = activeOldPlans.flatMap((plan) => plan.hiddenWaypoints || []);
            return { show: [], hide: oldHidden };
        }

        // ---- SHOW features (merged from all active plans)
        const show: string[] = activePlans
            .flatMap((plan) => [
                ...(pilotagePlanSettings.noGoAreas?.value ? plan.noGoAreas || [] : []),
                ...(pilotagePlanSettings.safetyMargins?.value ? plan.safetyMargins || [] : []),

                ...(pilotagePlanSettings.referencePoints?.value
                    ? plan.markerPoints?.filter((x) => !plan.hiddenWaypoints?.includes(x)) || []
                    : []),

                ...(pilotagePlanSettings.route?.value && plan.route ? [plan.route] : []),
                ...(pilotagePlanSettings.xtd?.value && plan.XTD ? [plan.XTD] : []),
                ...(pilotagePlanSettings.manoeuvreAreas?.value && plan.XTD ? [plan.manoeuvreAreas] : []),
            ])
            .flat();

        // ---- HIDE features (merged from all active plans)
        const hide: string[] = activePlans.flatMap((plan) => plan.hiddenWaypoints || []);

        return {
            show: Array.from(new Set(show)), // remove duplicates
            hide: Array.from(new Set(hide)), // remove duplicates
        };
    };

    updatePilotagePlans = (oldPilotagePlansState: PlanType[]) => {
        console.log(oldPilotagePlansState);

        let show: any[] = [];
        let hide: any[] = [];
        let areaFeatures: GeoJSONFeature[] = [];

        areaFeatures = clone(this.state.areaFeatures);
        ({ show, hide } = this.getActivePlanFeatures(oldPilotagePlansState));

        console.log(show, hide);

        this.setState(
            //@ts-ignore
            {
                areaFeatures: areaFeatures.map((feature) => {
                    const name = feature.properties?.name;
                    const key = feature.properties?.key;
                    const layer = feature.properties?.layer;

                    //@ts-ignore
                    if (!feature.properties.withPlan  ) {
                        return feature;
                    }

                    // if (layer === 'markerPointsLayer') {
                    //     let visible = feature.properties?.visible;

                    //     if (hide.includes(name)) {
                    //         visible = false;
                    //     } else if (show.includes(name)) {
                    //         visible = true;
                    //     }

                    //     return {
                    //         ...feature,
                    //         properties: { ...feature.properties, visible },
                    //     };
                    // }

                    // --- 2) Default logic for other layers ---
                    return {
                        ...feature,
                        properties: {
                            ...feature.properties,
                            visible: key ? show.includes(key) : show.includes(name),
                        },
                    };
                }),
            },
            this.renderPortAreas
        );
    };

    portAreaFeatureVisiblity: PortAreaFeatureVisiblityInterface = (
        names: string[] | string,
        type: string | string[],
        visible?: boolean,
        render?: boolean
    ) => {
        const { areaFeatures } = this.state;
        const areaFeature = areaFeatures.find((areaFeature: GeoJSONFeature) => {
            return (
                (typeof names === 'string'
                    ? names === areaFeature.properties?.name
                    : names.includes(areaFeature.properties?.name) &&
                      (areaFeature.properties?.type === type || type.includes(areaFeature.properties?.type))) ||
                (typeof names === 'string'
                    ? names === areaFeature.properties?.key
                    : names.includes(areaFeature.properties?.key))
            );
        });
        console.log(areaFeature);
        if (visible === undefined) {
            return {
                visible: areaFeature?.properties?.visible,
                name: areaFeature?.properties.name,
                type: areaFeature?.properties.type,
            };
        }
        this.setState(
            {
                areaFeatures: areaFeatures.map((areaFeature) => {
                    if (
                        (typeof names === 'string'
                            ? names === areaFeature.properties?.name
                            : names.includes(areaFeature.properties?.name) &&
                              (areaFeature.properties?.type === type || type.includes(areaFeature.properties?.type))) ||
                        (typeof names === 'string'
                            ? names === areaFeature.properties?.key
                            : names.includes(areaFeature.properties?.key))
                    ) {
                        return {
                            type: areaFeature.type,
                            geometry: areaFeature.geometry,
                            properties: { ...areaFeature.properties, visible },
                        };
                    }
                    return areaFeature;
                }),
            },
            () => {
                if (render) {
                    this.renderPortAreas();
                }
            }
        );
    };

    /**
     * Creates GeoJSON features for all port areas and updates the component state.
     */
    createPortAreas = (systemHasPlans: boolean) => {
        const areaFeatures: GeoJSONFeature[] = [];
        this.props.port?.port_areas?.forEach((portArea: PortAreaType) => {
            const { status, feature } = this.createPortArea(portArea.id, systemHasPlans);
            if (status && feature) {
                areaFeatures.push(feature);
            }
        });

        //@ts-ignore
        this.setState({ areaFeatures }, this.renderPortAreas);
    };

    moveTooltipsWithTime = (
        orders: {
            position: PositionType;
            layer: string;
            parent: string | number;
        }[]
    ) => {
        console.log('moveTooltipsWithTime');
        // Create a quick-access map from layer+parent to order
        const ordersMap = new Map<
            string,
            {
                position: PositionType;
                layer: string;
                parent: string | number;
            }
        >();

        for (const order of orders) {
            const key = `${order.layer}-${order.parent}`;
            ordersMap.set(key, order);
        }

        // Create the new tooltips state
        const updatedTooltips = this.state.tooltips.map((tooltip: ToolTipType) => {
            const key = `${tooltip.data.layer}-${tooltip.data.parent}`;
            const order = ordersMap.get(key);

            if (!order) {
                return tooltip;
            }

            const coordinate_deg: CoordinateArray = this.geometry.transformer([
                order.position.east_utm,
                order.position.north_utm,
            ]);

            const coordinate_xy = this.projectCoord(coordinate_deg);

            const x = coordinate_xy[0] + tooltip.xOffset;
            const y = coordinate_xy[1] + tooltip.yOffset;

            const { longitude, latitude } = this.getCoordinatesFromScreenXY(x, y);
            return {
                ...tooltip,
                dataIndex: {
                    ...tooltip.dataIndex,
                    time: order.position.valid_time_utc,
                    index: order.position.id,
                },
                longitude: longitude,
                latitude: latitude,
                east_utm: order.position.east_utm,
                north_utm: order.position.north_utm,
                x: x + TOOLTIP_OFFSET,
                y: y + TOOLTIP_OFFSET,
            };
        });

        // Update the state only once
        this.setState({ tooltips: updatedTooltips });
    };

    moveTooltipWithTime: MoveTooltipWithTimeInterface = (
        position: PositionType,
        layer: string,
        parent: string | number
    ) => {
        console.log('moveTooltipWithTime');
        const findToolTip = this.state.tooltips.find((x) => {
            return x.data.parent === parent && x.data.layer === layer;
        });

        if (!findToolTip) {
            return;
        }

        const coordinate_deg: CoordinateArray = this.geometry.transformer([position.east_utm, position.north_utm]);

        const coordinate_xy = this.projectCoord(coordinate_deg);

        const x = findToolTip.xOffset ? coordinate_xy[0] + findToolTip.xOffset : coordinate_xy[0] + TOOLTIP_OFFSET;
        const y = findToolTip.yOffset ? coordinate_xy[1] + findToolTip.yOffset : coordinate_xy[1] + TOOLTIP_OFFSET;
        const { longitude, latitude } = this.getCoordinatesFromScreenXY(x, y);
        this.setState({
            tooltips: this.state.tooltips.map((tooltip: ToolTipType) => {
                if (tooltip.data.parent === parent && tooltip.data.layer === layer) {
                    return {
                        ...tooltip,
                        dataIndex: { ...tooltip.dataIndex, time: position.valid_time_utc, index: position.id },
                        longitude: longitude,
                        latitude: latitude,
                        east_utm: position.east_utm,
                        north_utm: position.north_utm,
                        x,
                        y,
                    };
                }
                return tooltip;
            }),
        });
    };

    removeDrawingToolTipById: removeDrawingToolTipByIdInterface = (id: string) => {
        console.log(`INFO: Hiding drawing with tooltip ID: ${id}`);

        return new Promise<void>((resolve) => {
            this.setState(
                //@ts-ignore
                {
                    drawingFeatures: {
                        type: 'FeatureCollection',
                        features: this.state.drawingFeatures.features.map((feature) => {
                            if (feature.properties.id === id) {
                                return { ...feature, properties: { ...feature.properties, tooltip: false, name: '' } };
                            }
                            return feature;
                        }),
                    },
                },
                () => {
                    // Callback after setState is done
                    resolve();
                }
            );
        });
    };

    removeDrawingById: RemoveDrawingByIdInterface = (id: string) => {
        console.log(`INFO: Hiding drawing with ID: ${id}`);

        return new Promise<void>((resolve) => {
            this.setState(
                //@ts-ignore
                {
                    drawingFeatures: {
                        type: 'FeatureCollection',
                        features: this.state.drawingFeatures.features.filter((feature) => {
                            return feature.properties.id !== id;
                        }),
                    },
                },
                () => {
                    // Callback after setState is done
                    resolve();
                }
            );
        });
    };

    projectCoord = (coordinate: CoordinateArray): CoordinateArray => {
        const viewState = this.state.map.viewManager.getViewState();
        const viewport = new WebMercatorViewport(viewState);
        //@ts-ignore
        const XY = viewport.project(coordinate);
        return XY.map((i) => Math.round(i)) as unknown as CoordinateArray;
    };

    getCoordinatesFromScreenXY = (x: number, y: number) => {
        const viewState = this.state.map.viewManager.getViewState(); // Active view state
        const viewport = new WebMercatorViewport(viewState);
        const [longitude, latitude] = viewport.unproject([x, y]);
        return { longitude, latitude };
    };

    createCoordinateTooltip = (): Promise<void> => {
        return new Promise((resolve) => {
            if (!this.state.drawingFeatures.features.length || !this.state.drawingMode.includes('coordinate')) {
                return resolve();
            }
            const tooltips = clone(this.state.tooltips);
            clone(this.state.drawingFeatures)
                .features.filter((feature) => {
                    return feature.properties.type === 'coordinateMode' && feature.properties.tooltip === undefined;
                })
                .forEach((feature) => {
                    const tooltipExists = tooltips.some((tooltip) => tooltip.uuid === feature.properties.id);
                    if (tooltipExists) {
                        tooltips.forEach((tooltip) => {
                            if (tooltip.uuid === feature.properties.id) {
                                tooltip.data = {
                                    ...tooltip.data,
                                    name: feature.properties.name,
                                };
                            }
                        });
                    } else {
                        feature.geometry.coordinates = this.geometry.transformer(
                            feature.geometry.coordinates,
                            'EPSG:4326',
                            this.props.port.projection
                        );

                        const coordinate_utm: CoordinateArray = this.geometry.getMidpoint(feature);
                        const coordinate_deg: CoordinateArray = this.geometry.transformer(coordinate_utm);
                        const coordinate_xy = this.projectCoord(coordinate_deg);

                        const tooltip = {
                            static: true,
                            uuid: feature.properties.id,
                            type: 'coordinate',
                            event: 'onClick',
                            bind: true,
                            longitude: coordinate_deg[0],
                            latitude: coordinate_deg[1],
                            east_utm: coordinate_utm[0],
                            north_utm: coordinate_utm[1],
                            x: coordinate_xy[0] + TOOLTIP_OFFSET,
                            y: coordinate_xy[1] + TOOLTIP_OFFSET,
                            visible: true,
                            data: {
                                longitude: coordinate_deg[0],
                                latitude: coordinate_deg[1],
                                name: feature.properties.name,
                            },
                        };
                        tooltips.push(tooltip);
                    }
                });

            //@ts-ignore
            this.setState({ tooltips: tooltips }, () => {
                resolve();
            });
        });
    };

    createLabelTooltip = (): Promise<void> => {
        return new Promise((resolve) => {
            if (!this.state.drawingFeatures.features.length || !this.state.drawingMode.includes('label')) {
                return resolve();
            }

            const tooltips = clone(this.state.tooltips);

            clone(this.state.drawingFeatures).features.forEach((feature) => {
                const tooltipExists = tooltips.some((tooltip) => tooltip.uuid === feature.properties.id);

                if (tooltipExists) {
                    tooltips.forEach((tooltip) => {
                        if (tooltip.uuid === feature.properties.id) {
                            tooltip.data = {
                                ...tooltip.data,
                                name: feature.properties.name,
                            };
                        }
                    });
                } else if (feature.properties.name) {
                    // Assume geometry is a Point for labeling
                    switch (feature.geometry.type) {
                        case 'Point':
                            feature.geometry.coordinates = this.geometry.transformer(
                                feature.geometry.coordinates,
                                'EPSG:4326',
                                this.props.port.projection
                            );
                            break;

                        case 'LineString':
                            feature.geometry.coordinates = feature.geometry.coordinates.map((x: CoordinateArray) => {
                                return this.geometry.transformer(x, 'EPSG:4326', this.props.port.projection);
                            });
                            break;

                        case 'Polygon':
                            feature.geometry.coordinates = feature.geometry.coordinates.map(
                                (ring: CoordinateArray[]) => {
                                    return ring.map((x: CoordinateArray) => {
                                        return this.geometry.transformer(x, 'EPSG:4326', this.props.port.projection);
                                    });
                                }
                            );
                            break;

                        default:
                            throw new Error('Unsupported geometry type');
                    }

                    const coordinate_utm: CoordinateArray = this.geometry.getMidpoint(feature);
                    const coordinate_deg: CoordinateArray = this.geometry.transformer(coordinate_utm);
                    const coordinate_xy = this.projectCoord(coordinate_deg);

                    tooltips.push({
                        static: true,
                        uuid: feature.properties.id,
                        type: 'drawing',
                        event: 'onClick',
                        bind: true,
                        longitude: coordinate_deg[0],
                        latitude: coordinate_deg[1],
                        east_utm: coordinate_utm[0],
                        north_utm: coordinate_utm[1],
                        x: coordinate_xy[0] + TOOLTIP_OFFSET,
                        y: coordinate_xy[1] + TOOLTIP_OFFSET,
                        visible: true,
                        data: {
                            name: feature.properties.name,
                        },
                    });
                }
            });

            this.setState({ tooltips }, () => {
                resolve();
            });
        });
    };

    createMeasureTooltip = (): Promise<void> => {
        return new Promise((resolve) => {
            if (
                !this.state.drawingFeatures.features.filter((x) => {
                    return x.properties.type.includes('measure') && x.properties.tooltip === undefined;
                }).length ||
                !this.state.drawingMode.includes('measure')
            ) {
                return resolve();
            }

            const tooltips = clone(this.state.tooltips);
            clone(this.state.drawingFeatures)
                .features.filter((x) => {
                    return x.properties.type.includes('measure') && x.properties.tooltip === undefined;
                })
                .forEach((feature) => {
                    const tooltipExists = tooltips.some((tooltip) => tooltip.uuid === feature.properties.id);
                    if (tooltipExists) {
                        tooltips.forEach((tooltip) => {
                            if (tooltip.uuid === feature.properties.id) {
                                tooltip.data = {
                                    ...tooltip.data,
                                    name: feature.properties.name,
                                };
                            }
                        });
                    } else {
                        let results = {};
                        switch (feature.geometry.type) {
                            case 'Point':
                                feature.geometry.coordinates = this.geometry.transformer(
                                    feature.geometry.coordinates,
                                    'EPSG:4326',
                                    this.props.port.projection
                                );
                                break;
                            case 'LineString':
                                feature.geometry.coordinates = feature.geometry.coordinates.map(
                                    (x: CoordinateArray) => {
                                        return this.geometry.transformer(x, 'EPSG:4326', this.props.port.projection);
                                    }
                                );

                                results = {
                                    length: this.geometry.getLength(feature.geometry.coordinates as CoordinateArray[]),
                                    bearing: this.geometry.getBearing(
                                        feature.geometry.coordinates as CoordinateArray[]
                                    ),
                                };
                                break;

                            case 'Polygon':
                                feature.geometry.coordinates = feature.geometry.coordinates.map(
                                    (ring: CoordinateArray[]) => {
                                        return ring.map((x: CoordinateArray) => {
                                            return this.geometry.transformer(
                                                x,
                                                'EPSG:4326',
                                                this.props.port.projection
                                            );
                                        });
                                    }
                                );

                                results = {
                                    area: this.geometry.getArea(feature.geometry.coordinates as CoordinateArray[][]),
                                };
                                break;

                            default:
                                throw new Error('Unsupported geometry type');
                        }

                        if (Object.keys(results).length === 0) {
                            resolve();
                            return;
                        }

                        const coordinate_utm: CoordinateArray = this.geometry.getMidpoint(feature);
                        const coordinate_deg: CoordinateArray = this.geometry.transformer(coordinate_utm);
                        const coordinate_xy = this.projectCoord(coordinate_deg);

                        const tooltip = {
                            static: true,
                            uuid: feature.properties.id,
                            type: 'measure',
                            event: 'onClick',
                            bind: true,
                            longitude: coordinate_deg[0],
                            latitude: coordinate_deg[1],
                            east_utm: coordinate_utm[0],
                            north_utm: coordinate_utm[1],
                            x: coordinate_xy[0] + TOOLTIP_OFFSET,
                            y: coordinate_xy[1] + TOOLTIP_OFFSET,
                            visible: true,
                            data: {
                                type: feature.geometry.type === 'LineString' ? 'length' : 'area',
                                name: feature.properties.name,
                                results,
                            },
                        };

                        tooltips.push(tooltip);
                    }
                });

            //@ts-ignore
            this.setState({ tooltips: tooltips }, () => {
                resolve();
            });
        });
    };

    removeEditlayer = async () => {
        const { mapProps } = this.state;
        let layers = mapProps?.layers || [];
        layers = removeLayer(layers, this.props.layers.drawingLayer.uuid);
        await this.renderMap({
            layers: layers,
            controller: {
                doubleClickZoom: true,
                dragPan: true,
            },
        });
    };

    isSlecteMode = (): boolean => {
        return DRAWING_MODES[this.state.drawingMode].select || false;
    };
    isDragPanMode = (): boolean => {
        return DRAWING_MODES[this.state.drawingMode].dragPan || true;
    };

    resetDrawingLayer = async () => {
        await this.removeEditlayer();

        this.setState(
            //@ts-ignore
            {
                hoverMeasureData: null,
            },
            this.createEditlayer
        );
    };

    createEditlayer = async () => {
        const { mapProps, drawingFeatures, drawingMode } = this.state;
        let layers = mapProps?.layers || [];

        const mode = DRAWING_MODES[drawingMode];
        if (!mode) {
            //@ts-ignore
            this.setState({
                editType: null,
            });
            return;
        }
        layers = this.replaceLayers(
            layers,
            [
                //@ts-ignore
                new EditableGeoJsonLayer({
                    id: this.props.layers.drawingLayer.uuid,
                    index: 300,

                    data: drawingFeatures,

                    /* =========================
                     * DRAW / EDIT MODE
                     * ========================= */
                    mode: new mode.handler({
                        ...mode.options.handlerConfig,
                    }),
                    /* =========================
                     * SNAP CONFIGURATION
                     * ========================= */
                    ...mode.options.modeConfig,
                    snapTargets: drawingFeatures, // snap to already drawn features

                    /* =========================
                     * SELECTION
                     * ========================= */
                    selectedFeatureIndexes:
                        this.state.drawingMode === 'pointer'
                            ? []
                            : drawingFeatures.features
                                  .map((feature: any, index: number) => (feature.properties.selected ? index : null))
                                  .filter((index: number | null) => index !== null),

                    /* =========================
                     * STYLING
                     * ========================= */
                    getFillColor: (feature: any): RGBAColor =>
                        feature.properties.selected ? [255, 0, 0, 128] : [...hexToRgb(DRAWING_LINE_COLOR), 128],

                    getLineColor: (feature: any): RGBAColor =>
                        feature.properties.selected ? [255, 0, 0, 255] : [...hexToRgb(DRAWING_LINE_COLOR), 255],

                    /* =========================
                     * CLICK HANDLING
                     * ========================= */
                    onClick: (info: any) => {
                        const { object } = info;

                        if (this.state.drawingMode === 'pointer') {
                            this.renderMap({
                                controller: {
                                    doubleClickZoom: false,
                                    dragPan: true,
                                },
                            });
                        }

                        if (!object) {
                            this.renderMap({
                                controller: {
                                    doubleClickZoom: false,
                                    dragPan: true,
                                },
                            });
                            return;
                        }

                        const clickedFeatureId = object.properties.id;

                        /* -------- LABEL MODE -------- */
                        if (this.state.drawingMode === 'labelMode') {
                            this.props.openModelFunction(
                                'labelDrawingModel',
                                0,
                                'medium',
                                {
                                    id: clickedFeatureId,
                                    name: object.properties.name,
                                },
                                {
                                    onCancel: () => {
                                        this.props.openModelFunction('labelDrawingModel');
                                    },
                                    onConfirm: (data: any) => {
                                        this.updateDrawinglabel(data.id, data.name);
                                    },
                                },
                                true
                            );
                            return;
                        }

                        /* -------- ERASE MODE -------- */
                        if (this.state.drawingMode === 'eraseMode') {
                            this.props.openModelFunction(
                                'eraseDrawingModel',
                                0,
                                'medium',
                                {
                                    id: clickedFeatureId,
                                },
                                {
                                    onCancel: () => {
                                        this.props.openModelFunction('eraseDrawingModel');
                                    },
                                    onConfirm: (data: any) => {
                                        this.removeDrawingById(data.id);
                                    },
                                },
                                true
                            );
                            return;
                        }

                        /* -------- SELECT MODE -------- */
                        //@ts-ignore
                        this.setState((prevState: any) => {
                            const updatedFeatures = prevState.drawingFeatures.features.map((feature: any) => ({
                                ...feature,
                                properties: {
                                    ...feature.properties,
                                    selected:
                                        feature.properties.id === clickedFeatureId && this.isSlecteMode()
                                            ? !feature.properties.selected
                                            : feature.properties.selected,
                                },
                            }));

                            const selectedCount = updatedFeatures.filter((f: any) => f.properties.selected).length;

                            this.renderMap({
                                controller: {
                                    doubleClickZoom: false,
                                    dragPan: this.isDragPanMode() || selectedCount === 0,
                                },
                            });

                            return {
                                hoverMeasureData: null,
                                drawingFeatures: {
                                    type: 'FeatureCollection',
                                    features: updatedFeatures,
                                },
                            };
                        });
                    },

                    /* =========================
                     * EDIT HANDLING
                     * ========================= */
                    onEdit: ({ updatedData }: any) => {
                        const newFeatures = updatedData?.features || [];
                        //@ts-ignore
                        this.setState((prevState: any) => {
                            const currentFeatures = prevState.drawingFeatures.features || [];

                            const processedFeatures = newFeatures.map((newFeature: any) => {
                                if (!newFeature.properties.id) {
                                    newFeature.properties.id = getId();
                                    newFeature.properties.selected = false;
                                    newFeature.properties.type = this.state.drawingMode;
                                }
                                return newFeature;
                            });

                            const updatedFeatures = currentFeatures.map((feature: any) => {
                                const updatedFeature = processedFeatures.find(
                                    (f: any) => f.properties.id === feature.properties.id
                                );
                                return updatedFeature || feature;
                            });

                            processedFeatures.forEach((newFeature: any) => {
                                if (!currentFeatures.some((f: any) => f.properties.id === newFeature.properties.id)) {
                                    updatedFeatures.push(newFeature);
                                }
                            });

                            return {
                                hoverMeasureData: null,
                                drawingFeatures: {
                                    type: 'FeatureCollection',
                                    features: updatedFeatures,
                                },
                            };
                        });
                    },
                }),
            ],
            true
        );

        await this.renderMap({
            layers: layers,
            controller: {
                doubleClickZoom: mode === 'pointer',
                dragPan: this.state.mapProps.controller.dragPan,
            },
        });
    };

    updateDrawinglabel = (id: string, name: string) => {
        this.setState(
            //@ts-ignore
            {
                drawingFeatures: {
                    type: 'FeatureCollection',
                    features: clone(this.state.drawingFeatures).features.map((x) => {
                        if (x.properties.id === id) {
                            x.properties.name = name;
                        }
                        return x;
                    }),
                },
            }
        );
    };

    getDrawingFeatures: GetDrawingFeaturesInterface = () => {
        return clone(this.state.drawingFeatures);
    };

    // renderDrawinglayers = async () => {
    //     const { mapProps, drawingFeatures } = this.state;
    //     let layers = mapProps?.layers || [];

    //     layers = this.replaceLayers(layers, [
    //         new GeoJsonLayer({
    //             id: LAYERS.drawingLayer.uuid,
    //             name: LAYERS.drawingLayer.name,
    //             data: drawingFeatures, // Your GeoJSON data
    //             index: LAYERS.drawingLayer.drawIndex,
    //             pickable: true,
    //             stroked: true, // Enable stroked borders for the polygons
    //             filled: true, // Enable filling for the polygons
    //             extruded: false,
    //             pointRadiusScale: 10,

    //             // Fill color: red for selected, otherwise based on feature properties
    //             getFillColor: (feature) => {
    //                 if (feature.properties.selected) {
    //                     return [255, 0, 0, 128]; // Red with opacity for selected
    //                 }
    //                 const { fill } = feature.properties;
    //                 return feature.properties.visible ? [...hexToRgb(fill.colour), fill.opacity * 255] : [0, 0, 0, 0]; // Transparent if not visible
    //             },

    //             // Line color: red for selected, otherwise based on feature properties
    //             getLineColor: (feature) => {
    //                 if (feature.properties.selected) {
    //                     return [255, 0, 0, 255]; // Red for selected
    //                 }
    //                 const { stroke } = feature.properties;
    //                 return feature.properties.visible
    //                     ? [...hexToRgb(stroke.colour), stroke.opacity * 255]
    //                     : [0, 0, 0, 0]; // Transparent if not visible
    //             },

    //             // Line width based on visibility
    //             getLineWidth: (feature) => (feature.properties.visible ? feature.properties.stroke.width || 1 : 0),
    //         }),
    //     ]);

    //     await this.renderMap({ layers: layers });
    // };

    updateTooltips = (x: number, y: number, uuid: string | null) => {
        //@ts-ignore
        this.setState({
            tooltips: this.state.tooltips.map((tooltip: ToolTipType) => {
                if (tooltip.uuid === uuid) {
                    const { longitude, latitude } = this.getCoordinatesFromScreenXY(x, y);

                    const sourceCoordinateXY = this.projectCoord(
                        this.geometry.transformer([tooltip.east_utm, tooltip.north_utm])
                    );

                    return {
                        ...tooltip,
                        x,
                        y,
                        xOffset: x - sourceCoordinateXY[0],
                        yOffset: y - sourceCoordinateXY[1],
                        longitude,
                        latitude,
                    };
                }
                return tooltip;
            }),
        });
    };

    clearMap = (clearAll = true): void => {
        window?.log('INFO : clear map');
        this.setState(
            //@ts-ignore
            {
                markingFeatures: [],
                drawingFeatures: {
                    type: 'FeatureCollection',
                    features: clearAll
                        ? []
                        : this.state.drawingFeatures.features.filter((x) => {
                              return !x.properties.selected;
                          }),
                },
            },
            async () => {
                await this.renderMap({
                    controller: {
                        doubleClickZoom: true,
                        dragPan: true,
                    },
                });

                await this.createEditlayer();
                await this.renderMarkinglayers();
            }
        );
    };

    /**
     * Creates GeoJSON features for all port areas and updates the component state.
     */
    createDrawingLayer: CreateDrawingLayerInterface = () => {
        this.setState(
            //@ts-ignore
            {
                drawingFeatures: {
                    type: 'FeatureCollection',
                    features: [],
                },
            }
        );
        return;
    };

    /**
     * Creates GeoJSON features for all port areas and updates the component state.
     */
    createMarkingLayer: CreateMarkingLayerInterface = (features: GeoJSONFeature[], clear = false, zoomTo = true) => {
        const markingFeatures: (GeoJSONFeature | null)[] = features.map((feature) => {
            return updateFeatureProperties(
                0,
                feature,
                {
                    index: 0,
                    drawIndex: this.props.layers.markingLayer.drawIndex,
                    fillColorUpdate: () => {
                        return { colour: MARKING_LINE_COLOR, opacity: 1 };
                    },
                    strokeColorUpdate: () => {
                        return { colour: MARKING_LINE_COLOR, opacity: 1 };
                    },

                    fill: { colour: MARKING_LINE_COLOR, opacity: 1 },
                    stroke: { colour: MARKING_LINE_COLOR, opacity: 1, width: 5 },
                    display: true,
                    visible: true,
                    layer: LAYERS.markingLayer.name,
                    description: null,
                },
                LAYERS.markingLayer.name
            );
        });
        if (clear) {
            //@ts-ignore
            this.setState({ markingFeatures }, () => {
                if (zoomTo) {
                    this.zoomToFetures(markingFeatures);
                }
                this.renderMarkinglayers();
            });
            return;
        }

        this.setState(
            //@ts-ignore
            { markingFeatures: [...this.state.markingFeatures, ...markingFeatures] },
            () => {
                if (zoomTo) {
                    this.zoomToFetures(markingFeatures);
                }
                this.renderMarkinglayers();
            }
        );
        return;
    };

    zoomToFetures = (features: (GeoJSONFeature | null)[]) => {
        const { map } = this.state;

        const [minX, minY, maxX, maxY] = this.geometry.getBBoxFromGeoJSON(features);

        // Calculate the center of the bounding box
        const longitude = (minX + maxX) / 2;
        const latitude = (minY + maxY) / 2;

        // Retrieve current viewState from the map
        //@ts-ignore
        const currentViewState = map?.props?.viewState || {}; // Fallback to an empty object if undefined

        map?.setProps({
            viewState: {
                ...currentViewState, // Preserve current view state
                longitude,
                latitude,
                zoom: Math.min(
                    Math.log2(360 / (maxX - minX)), // Adjust zoom based on width
                    Math.log2(180 / (maxY - minY)) // Adjust zoom based on height
                ),
                transitionDuration: 1000, // Smooth transition duration
                transitionInterpolator: new FlyToInterpolator(), // Smooth fly-to effect
            },
        });
    };

    /**
     * Creates a styled GeoJSON feature for a specific port area.
     *
     * This function generates a GeoJSON feature for the given port area ID, applies
     * the appropriate style and layer configurations, and returns the result.
     *
     * Layer index ranges:
     * - 0 to 10: Background layers
     * - 11 to 100: Port configuration areas
     * - 101 to 1000: Data layers
     * - 1001 to 1100: Drawing layers
     *
     * @param id - The unique identifier for the port area.
     * @returns An object containing the status and the updated GeoJSON feature.
     */
    createPortArea = (id: number, systemHasPlans) => {
        const portAreas = this.props.port.port_areas;
        const portArea = portAreas?.find((portArea) => portArea.id === id);

        if (!portArea) {
            this.props.showMessageAction({ messageType: 'ERROR', message: translate('Port area item does not exist') });
            return { status: true, feature: null };
        }

        let type = portArea.type;
        if (type === 'marker' && portArea.parent === null) {
            type = 'markerPoints';
        } else if (type === 'wayPoint' && portArea.parent !== null) {
            type = 'wayPoint';
        }

        const layer = this.props.layers[type];
        const configMapStyle = this.props.port.meta_data.colours[layer?.style || 'features'];
        if (!layer || !configMapStyle) {
            console.log(`INFO: Port area type ${type} has no style map.`);
            return { status: true, feature: null };
        }

        const { show, hide } = this.getActivePlanFeatures([]);

        const feature = updateFeatureProperties(
            id,
            this.geometry.transformerGEOJSON(this.geometry.parseWKB(portArea.geo)),
            portArea,
            type
        );
        if (!feature) {
            console.log(`INFO: featre ${type}  ${layer.name} has no data .`);
            return { status: true, feature: null };
        }
        return {
            status: true,
            feature: updatePortAreaFeatureStyle(
                this.state.switchs,
                feature,
                type,
                layer.name,
                layer.drawIndex || 0,
                layer.clickIndex || 0,
                systemHasPlans && layer.withPlan ? layer.withPlan : false,
                configMapStyle,
                show
            ),
        };
    };

    getDrawingMode = (): string => {
        return this.state.drawingMode;
    };

    setDrawingMode: SetDrawingModeInterface = (mode: string) => {
        let previousMode: string | null = null; // Variable to track the previous mode

        this.setState(
            //@ts-ignore
            (prevState) => {
                previousMode = prevState.drawingMode; // Store the previous mode
                if (prevState.drawingMode === mode) {
                    return null; // No update if the mode is the same
                }
                return {
                    drawingMode: mode,
                    hoverMeasureData: null,
                    drawingFeatures: {
                        type: 'FeatureCollection',
                        features: this.state.drawingFeatures.features.map((feature) => {
                            return {
                                ...feature,
                                properties: {
                                    ...feature.properties,
                                    selected: false,
                                },
                            };
                        }),
                    },
                };
            },
            () => {
                console.log(previousMode, mode);
                // Check if the mode actually changed
                if (previousMode !== mode && mode !== '') {
                    this.createEditlayer();

                    window.log(`INFO: updated drawing mode from ${previousMode} to ${mode}`);
                } else if (previousMode?.includes('draw') && previousMode === mode) {
                    window.log(`INFO: reset drawing mode from ${previousMode} to ${mode}`);
                    this.resetDrawingLayer();
                }
            }
        );
    };

    /**
     * Renders the port areas on the map by updating the layers based on the features' properties.
     *
     * This function utilizes Deck.gl's `GeoJsonLayer` to dynamically create layers for rendering
     * GeoJSON features. Each layer corresponds to a unique feature layer, with styles and properties
     * defined by the feature's attributes.
     *
     * Features:
     * - Dynamically replaces the map layers based on the `areaFeatures` and their associated layers.
     * - Filters features for each layer by `properties.layer`.
     * - Configures styling for fill, stroke, and visibility based on feature properties.
     * - Uses `updateTriggers` to re-render only when `areaFeatures` change.
     *
     * @returns {void}
     */
    renderPortAreas = async () => {
        const { mapProps } = this.state;
        let layers = mapProps?.layers || [];
        layers = this.replaceLayers(
            layers,
            Object.values(this.props.layers)
                .filter((layer: LayerTypeData) => {
                    return layer.group === 'portAreas';
                })
                .map((layer: LayerTypeData) => {
                    return new GeoJsonLayer<GeoJSONFeature>({
                        id: layer.uuid,
                        //@ts-ignore
                        name: layer.name,
                        data: this.state.areaFeatures.filter((x: GeoJSONFeature) => {
                            return x.properties?.layer === layer.name;
                        }),
                        index: layer.drawIndex,
                        pickable: true,
                        //@ts-ignore
                        stroked: true, // Enable stroked borders for the polygons
                        //@ts-ignore
                        filled: true, // Enable filling for the polygons
                        extruded: false,
                        pointRadiusScale: 10,
                        lineWidthUnits: 'pixels',
                        getFillColor: (feature): RGBAColor => {
                            return feature?.properties?.visible
                                ? [...hexToRgb(feature.properties?.fill.colour), feature.properties?.fill.opacity * 255]
                                : [0, 0, 0, 0]; // Transparent if not visible
                        },
                        getLineColor: (feature): RGBAColor => {
                            //@ts-ignore
                            return feature?.properties?.visible
                                ? [
                                      ...hexToRgb(feature.properties?.stroke.colour),
                                      feature.properties?.stroke.opacity * 255,
                                  ]
                                : [0, 0, 0, 0]; // Transparent if not visible
                        },
                        getLineWidth: (feature) =>
                            feature.properties?.visible ? feature.properties?.stroke.width || 1 : 0, // 0 width hides the stroke

                        getLineOpacity: (feature) => {
                            return feature.properties?.stroke ? feature.properties?.stroke.opacity || 1 : 1; // Default opacity: 1 if not provided
                        },
                    }) as unknown as DeckGLLayer;
                })
        );

        await this.renderMap({ layers: layers });
    };

    renderMarkinglayers = async () => {
        const { mapProps } = this.state;
        let layers = mapProps?.layers || [];

        layers = this.replaceLayers(layers, [
            new GeoJsonLayer<GeoJSONFeature>({
                id: LAYERS.markingLayer.uuid,
                // @ts-ignore
                name: LAYERS.markingLayer.name,
                data: this.state.markingFeatures,
                index: LAYERS.markingLayer.drawIndex,
                pickable: true,
                stroked: true,
                filled: true,
                extruded: false,
                pointRadiusScale: 10,
                lineWidthUnits: 'pixels',
                getFillColor: (feature) =>
                    feature.properties?.visible
                        ? [...hexToRgb(feature.properties?.fill.colour), feature.properties?.fill.opacity * 255]
                        : [0, 0, 0, 0],
                getLineColor: (feature) =>
                    feature.properties?.visible
                        ? [...hexToRgb(feature.properties?.stroke.colour), feature.properties?.stroke.opacity * 255]
                        : [0, 0, 0, 0],
                getLineWidth: (feature) => (feature.properties?.visible ? feature.properties?.stroke.width || 1 : 0),
                getLineOpacity: (feature) => (feature.properties?.stroke ? feature.properties?.stroke.opacity || 1 : 1),
            }) as unknown as DeckGLLayer,
        ]);
        await this.renderMap({ layers: layers });
    };
}
